import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from '../../../localization/useTranslation';
import { TextInput, ValidatedOptions, Tooltip } from '@patternfly/react-core';

import SimpleTable, { SortDirection } from '../../Common/SimpleTable/SimpleTable';
// TODO: move to SelectV2
import Selector from 'components/Selector/Selector';
import Icons from 'components/Common/Icons';
import useMeasurementSystem from 'components/Value/useMeasurementSystem';
import createConvertFunctions from 'components/Value/createConvertFunctions';

const DefaultSortBy = {
  path: 'name',
  direction: SortDirection.DESC
};

const booleanOptions = [
  { value: true, name: 'True' },
  { value: false, name: 'False' }
];
/*
const renderValue = (value, schema) => {
  if (_.isNil(value)) {
    return '';
  }
  switch (schema) {
    case 'string': return _.isString(value) ? value : value.toString();
    case 'long':
    case 'integer': return _.isInteger(value) ? value : Number(value);
    case 'float':
    case 'double': return _.isNumber(value) ? value : Number(value);
    case 'boolean': return _.isBoolean(value) ? _.capitalize(value.toString()) : _.capitalize(!!value);
    default: throw Error(`Unsupported prop value schema encountered (${schema})`);
  }
};
*/
const getFormattedValue = (value, schema) => {
  switch (schema) {
    case 'string': return _.isString(value) ? value : value.toString();
    case 'integer':
    case 'long':
      return _.isInteger(value) ? value : _.parseInt(value);
    case 'double':
    case 'float':
      return _.isNumber(value) ? value : parseFloat(value);
    case 'boolean':
      return _.first(value)?.value;
    default: throw Error(`Unsupported prop value schema encountered (${schema})`);
  }
};

const validateRow = (schema, value) => {
  switch (schema) {
    case 'string':
      return (_.isString(value)) ? ValidatedOptions.success : ValidatedOptions.error;
    case 'integer':
    case 'long':
    case 'double':
    case 'float':
      return (_.isNumber(Number(value)) && !_.isNaN(Number(value))) ? ValidatedOptions.success : ValidatedOptions.error;
      // return (Number.isSafeInteger(Number(value))) ? ValidatedOptions.success : ValidatedOptions.error;
    case 'boolean':
      return (_.isBoolean(_.first(value)?.value) || (_.isEmpty(value))) ? ValidatedOptions.success : ValidatedOptions.error;
    default:
      return ValidatedOptions.success;
  }
};

const DesiredEdit = ({ row, onDesiredChange }) => {
  let validation = validateRow(row.schema, row.desired);
  const change = (value) => {
    validation = validateRow(row.schema, value);
    onDesiredChange(row, value);
  };
  if (row.writable === false) {
    // return {T.common.readonly}
    return 'readonly';
  }
  // TODO add date, time etc as schemas that will render other inputs, booleans should probably be a dropdown as well
  switch (row.schema) {
    case 'string':
    case 'float':
    case 'double':
    case 'integer':
    case 'long':
      return <TextInput id={row.name} value={row.desired ?? ''} onChange={(_event, value) => change(value)} validated={validation}/>;
    case 'boolean':
      const selectedItem = _.isBoolean(row.desired) ? _.find(booleanOptions, { value: row.desired }) : _.first(row.desired);
      return <Selector id={row.name} items={booleanOptions} selectedItem={selectedItem?.name} onSelect={change}/>;
    default:
      return <TextInput id={row.name} value={row.desired ?? ''} onChange={(_event, value) => change(value)} validated={validation}/>;
  }

};

const AssetPropList = ({ propModels, moduleId, updateProps, isUpdatingProps, refetchReported }) => {
  const [convertedDesired, setConvertedDesired] = useState([]);
  const T = useTranslation();
  const { convertToUserValue, convertToSystemValue, units } = useMeasurementSystem({ undefinedValueString: '' });

  const desired = useMemo(() => {
    return _.map(propModels, prop => {
      const editedProp = _.find(convertedDesired, { name: prop.name });
      if (editedProp) {
        return { ...prop, desired: editedProp.desired };
      }
      return { ...prop, desired: convertToUserValue({ value: prop.desired, schemaUnit: prop }) };
    });
  }, [convertToUserValue, convertedDesired, propModels]);

  const unitsChangedRef = useRef(null);

  // This effect will update the edited values to correct unit type
  useEffect(() => {

    if (unitsChangedRef.current !== units && unitsChangedRef.current !== null) {
      const { convertToSystemValue: toOldSystem } = createConvertFunctions(unitsChangedRef.current);
      const { convertToUserValue: toNewUserValue } = createConvertFunctions(units);
      setConvertedDesired(oldDesired => {
        return _.map(oldDesired, prop => {
          const schemaUnit = _.find(propModels, { name: prop.name });
          if (schemaUnit) {
            const oldValue = getFormattedValue(prop.desired, schemaUnit.schema);
            const value = toNewUserValue({ value: toOldSystem({ value: oldValue, schemaUnit }), schemaUnit });
            return { ...prop, desired: value };
          }
          return prop;
        });
      });
    }
    unitsChangedRef.current = units;
  }, [propModels, units]);
  const hasUpdated = convertedDesired.length > 0;

  const allDesiredValuesValid = useMemo(() => {
    return _.some(desired, prop => ((prop?.writable === true) ? validateRow(prop.schema, prop.desired) === ValidatedOptions.success : true));
  }, [desired]);

  const _updateProps = useCallback(() => {
    const props = _.map(convertedDesired, prop => {
      return { [prop.name]: getFormattedValue(convertToSystemValue({ value: prop.desired, schemaUnit: prop }), prop.schema) };
    });

    const mergedProps = _.merge({}, ...props);
    if (moduleId) {
      updateProps({ [moduleId]: mergedProps });
    } else {
      updateProps(mergedProps);
    }
  }, [convertToSystemValue, convertedDesired, moduleId, updateProps]);


  const onDesiredChange = useCallback((row, value) => {
    setConvertedDesired(oldProps => {
      const oldDesiredIndex = _.findIndex(oldProps, { '@id': row['@id'] });
      if (oldDesiredIndex !== -1) {
        // Update old changed property
        const newProp = oldProps[oldDesiredIndex];
        newProp.desired = value;
        return [...oldProps];
      }
      // Add new changed property
      const originalDesired = { ..._.find(desired, { '@id': row['@id'] }) };
      originalDesired.desired = value;
      return [...oldProps, originalDesired];
    });
  }, [desired]);

  const columns = useMemo(() => {
    return [
      {
        label: T.common.name,
        path: '.',
        width: 40,
        render: row => {
          if (!_.isEmpty(row?.description)) {
            return (
              <Tooltip
                content={
                  <div>
                    { row.description }
                  </div>
                }
              >
                <div>
                  { row.name }
                  <Icons.Info style={{ marginLeft: '8px' }}/>
                </div>
              </Tooltip>
            );
          }
          return row.name;
        }
      },
      {
        label: T.asset.property.desired,
        path: '.',
        width: 30,
        render: row => row.writable === true && <DesiredEdit row={row} onDesiredChange={onDesiredChange} />
      },
      {
        label: T.asset.property.reported,
        path: '.',
        width: 30,
        render: row => convertToUserValue({ value: row.reported, schemaUnit: row, showUnit: true })
      }
    ];
  }, [T.asset.property.desired, T.asset.property.reported, T.common.name, convertToUserValue, onDesiredChange]);

  return (
    <SimpleTable
      columns={columns}
      data={desired}
      globalActions={[
        { label: T.common.save, isLoading: isUpdatingProps, isDisabled: (!allDesiredValuesValid || !hasUpdated), onClick: _updateProps },
        { label: T.common.refresh, onClick: refetchReported }
      ]}
      sortBy={DefaultSortBy}
      compact
    />
  );
};

export default AssetPropList;
